import { createSlice } from "@reduxjs/toolkit";

import { DISTANCE_UNIT } from "../../constants/distance-unit";
import { MODES } from "../../constants/booking-modes";
import { EXTRANET_PRICING_MODES_ARRAY } from "../../constants/extranet-pricing-modes";

const initialStatusState = {
  isChanged: false,
  isError: false,
  isLoading: false,
  isDone: false,
};

const initialNewCityCreationForm = {
  country: "",
  cityName: "",
  currency: "",

  cityNameCorrect: false,
  currencyCorrect: false,

  cityExistErrorMessage: "",
  cityNameErrorMessage: "",
  currencyErrorMessage: "",

  distanceUnit: "km", // km | miles
  longDistancePricePerUnit: 0, // has to be here for api
};

const initialNewZoneCreationForm = {
  mode: "",

  pointA: {
    lat: null,
    lng: null,
    address: "",
    place_id: "",
  },

  pointB: {
    lat: null,
    lng: null,
    address: "",
    place_id: "",
  },

  pointACorrect: false,
  pointBCorrect: false,

  zoneName: "",
  zoneNameCorrect: false,

  errorMessage: "",
};

const initialState = {
  // helping variables
  newCityCreationMode: true,
  currentSelectedCityId: "",
  currentPricingView: MODES[0],
  oneWayZoneActiveIndex: null,

  // cities
  cities: {},
  citiesFetchingStatus: {
    isFetching: false,
    isFetched: false,
    isError: false,
  },

  // zones
  zones: {},
  zoneStatus: {},

  // Zones - Oneway Reducer

  // Zones - Hourly Reducer

  // Zones - Long Distance Reducer

  // form state
  newCityCreationForm: { ...initialNewCityCreationForm },
  newZoneCreationForm: { ...initialNewZoneCreationForm },
};

const pricingSlice = createSlice({
  name: "pricing",
  initialState,
  reducers: {
    resetPricingSlice: () => initialState,

    // Helping Variables Reducer
    setNewCityCreationMode(state, action) {
      state.newCityCreationMode = !!action.payload.mode;
    },

    selectCity(state, action) {
      const cityId = action.payload.cityId;
      const cityExists = Object.keys(state.cities).some((id) => id === cityId);

      if (cityExists || cityId === "") {
        state.currentSelectedCityId = cityId;
      }
    },

    setPricingViewMode(state, action) {
      const view = action.payload.view;

      if (!MODES.includes(view)) return;

      state.currentPricingView = view;
    },

    // City Reducer
    setCitiesFetchingStatus(state, action) {
      const { isFetching, isFetched, isError } = action.payload.status;

      state.citiesFetchingStatus.isFetching = isFetching;
      state.citiesFetchingStatus.isFetched = isFetched;
      state.citiesFetchingStatus.isError = isError;
    },

    setCities(state, action) {
      const cities = action.payload.allCities;
      state.cities = {};
      cities.map((city) => {
        state.cities[city._id] = {
          ...city,

          // Zone Fetch Status
          isZoneError: false,
          isZoneFetched: false,
          isZoneFetching: false,
          zoneRefetchCount: 0,
        };
      });
    },

    addCity(state, action) {
      const city = action.payload.city;
      state.cities[city._id] = {
        ...city,

        // Zone Fetch Status
        isZoneError: false,
        isZoneFetched: false,
        isZoneFetching: false,
        zoneRefetchCount: 0,
      };
    },

    deleteExistingCity(state, action) {
      const cityId = action.payload.cityId;

      if (!state.cities[cityId]) return;

      Object.keys(state.zones).forEach((zoneId) => {
        const zone = state.zones[zoneId];
        if (zone.cityId === cityId) {
          delete state.zones[zoneId];
        }
      });

      // Delete the city from the state
      delete state.cities[cityId];

      // Additional State Manage irrespective of delete
      if (Object.keys(state.cities).length < 1) {
        state.newCityCreationMode = true;
        state.currentSelectedCityId = "";
      } else {
        state.newCityCreationMode = false;
        state.currentSelectedCityId = Object.keys(state.cities)[0];
      }
    },

    setIsZoneError(state, action) {
      const { cityId, val } = action.payload; // cityId: string; val: boolean
      if (!state.cities[cityId]) return;
      state.cities[cityId].isZoneError = val;
    },

    setIsZoneFetching(state, action) {
      const { cityId, val } = action.payload; // cityId: string, val: boolean
      if (!state.cities[cityId]) return;
      state.cities[cityId].isZoneFetching = val;
    },

    setIsZoneFetched(state, action) {
      const { cityId, val } = action.payload; // cityId: string, val: boolean
      if (!state.cities[cityId]) return;
      state.cities[cityId].isZoneFetched = val;
    },

    incrementZoneRefetchCount(state, action) {
      const { cityId } = action.payload; // cityId: string
      if (!state.cities[cityId]) return;
      state.cities[cityId].zoneRefetchCount += 1;
    },

    // Zone
    changeZoneStatus: (state, action) => {
      const { newStatus, zoneId } = action.payload;
      // zoneId: string; newStatus: IsChangedStatus

      if (state.zoneStatus[zoneId])
        state.zoneStatus[zoneId] = {
          ...state.zoneStatus[zoneId],
          ...newStatus,
        };
    },

    resetZonePricingForm: (state) => {
      state.newZoneCreationForm = { ...initialNewZoneCreationForm };
    },

    // Zone Reducer One way
    addZone: (state, action) => {
      const newZoneId = action.payload.newZone._id;
      const newZone = {
        ...action.payload.newZone,
        pricing: action.payload.newZone.pricing ?? {},
        newCarCategory: action.payload.newZone.newCarCategory ?? {},
      };
      state.zones[newZoneId] = newZone;
      state.zoneStatus[newZoneId] = { ...initialStatusState };
    },

    setOnewayAccordianIndex: (state, action) => {
      state.oneWayZoneActiveIndex = action.payload.key; // string | null
    },

    addCarInZone: (state, action) => {
      const { zoneId, carName, carPrice } = action.payload;
      //     zoneId: string,
      //     carName: string,
      //     carPrice: OnewayForm | HourlyForm,

      if (!state.zones[zoneId]) return;
      if (!state.zones[zoneId].pricing) {
        state.zones[zoneId].pricing = {};
      }

      const newCarPrice = {};

      Object.entries(carPrice).forEach(([key, value]) => {
        const numericValue = isNaN(Number(value)) ? "0" : value;
        newCarPrice[key] = numericValue;
      });

      state.zones[zoneId].pricing[carName] = newCarPrice;
    },

    removeCarInZone: (state, action) => {
      const { zoneId, carName } = action.payload;
      // zoneId: string,
      // carName: string,

      if (!state.zones[zoneId]) return;

      if (!state.zones[zoneId].pricing) {
        state.zones[zoneId].pricing = {};
      }

      if (state.zones[zoneId].pricing[carName]) {
        delete state.zones[zoneId].pricing[carName];
      }
    },

    changeOneWayZoneForm: (state, action) => {
      const { zoneId, carName, carPrice } = action.payload;
      //     zoneId: string, carName: string, carPrice: OnewayForm,

      if (!state.zones[zoneId]) return;
      if (state.zones[zoneId].bookingType !== "ONEWAY") return;

      if (!state.zones[zoneId].pricing) {
        state.zones[zoneId].pricing = {};
      }

      if (!state.zones[zoneId].pricing[carName]) {
        state.zones[zoneId].pricing[carName] = { transferPrice: "" };
      }

      const newCarPrice = {};

      Object.entries(carPrice).forEach(([key, value]) => {
        const numericValue = isNaN(Number(value)) ? "" : value;
        newCarPrice[key] = numericValue;
      });

      state.zones[zoneId].pricing[carName] = newCarPrice;
    },

    removeZone: (state, action) => {
      const zoneId = action.payload.zoneId; // zoneId: string

      if (state.zones[zoneId]) delete state.zones[zoneId];
      if (state.zoneStatus[zoneId]) delete state.zoneStatus[zoneId];
    },

    // Zone Reducer Hourly
    changeHourlyZoneForm: (
      state,
      action
      //   : PayloadAction<{
      //     zoneId: string,
      //     carName: string,
      //     carPrice: HourlyForm,
      //   }>
    ) => {
      const { zoneId, carName, carPrice } = action.payload;

      if (!state.zones[zoneId]) return;
      // if (state.zones[zoneId].bookingType !== "HOURLY") return;

      if (!state.zones[zoneId].pricing) {
        state.zones[zoneId].pricing = {};
      }

      // const newCarPrice: Record<string, number> = {};
      const newCarPrice = {};
      //   : Record<string, string>
      Object.entries(carPrice).forEach(([key, val]) => {
        const value = Number(val);
        newCarPrice[key] = isNaN(value) ? "" : val;
      });

      state.zones[zoneId].pricing[carName] = newCarPrice;
    },

    // Zone Reducer Long Distance
    addLongDistanceRecord: (state, action) => {
      const payloadZoneId = action.payload.zoneId;
      const carName = action.payload.carName;

      const zones = state.zones;

      if (!zones[payloadZoneId]) return;

      if (
        !state.zones[payloadZoneId].pricing[carName] ||
        state.zones[payloadZoneId].pricing[carName].length === 0
      )
        state.zones[payloadZoneId].pricing[carName] = [
          { start: 0, end: 0, price: 10 },
          { start: 1, end: "INF", price: 10 },
        ];

      const ranges = [...zones[payloadZoneId].pricing[carName]];
      const infIndex = ranges.findIndex((r) => r.end === "INF");

      if (infIndex <= 0) return;

      const last = ranges[infIndex - 1];
      const newStart = last.end + 1;
      const newEnd = last.end + 10;

      const newRange = { start: newStart, end: newEnd, price: 10 };

      ranges.splice(infIndex, 0, newRange);

      // Adjust the INF record's start
      ranges[infIndex + 1] = { ...ranges[infIndex + 1], start: newEnd + 1 };

      zones[payloadZoneId].pricing[carName] = ranges;
    },

    removeLongDistanceRecord: (state, action) => {
      const { zoneId, carName, index } = action.payload;

      const zone = state.zones[zoneId];
      if (!zone || !zone.pricing[carName]) return;

      const ranges = [...zone.pricing[carName]];

      // Prevent removing if index is out of bounds or INF
      if (index < 0 || index >= ranges.length - 1) return;

      // Adjust next range start if exists
      if (ranges[index + 1]) {
        const prevEnd = index > 0 ? ranges[index - 1].end : -1;
        ranges[index + 1].start = prevEnd + 1;
      }

      // Remove the range
      ranges.splice(index, 1);

      // Save updated list back to state
      state.zones[zoneId].pricing[carName] = ranges;
      if (ranges.length === 1) state.zones[zoneId].pricing[carName] = [];
    },

    changeLongDistanceRecord: (state, action) => {
      const { zoneId, carName, index, key, value } = action.payload;

      const zone = state.zones[zoneId];
      if (!zone || !zone.pricing[carName]) return;

      const ranges = [...zone.pricing[carName]];
      if (!ranges[index]) return;

      // Update the selected range
      ranges[index] = {
        ...ranges[index],
        [key]: Number(value),
      };

      // If updating the last non-INF range's `end`, adjust INF's start
      const infIndex = ranges.findIndex((r) => r.end === "INF");
      if (index === infIndex - 1 && key === "end") {
        ranges[infIndex] = {
          ...ranges[infIndex],
          start: Number(value) + 1,
        };
      }

      state.zones[zoneId].pricing[carName] = ranges;
    },

    updateInfinityRecordPrice: (state, action) => {
      const { zoneId, carName, price } = action.payload;

      const zone = state.zones[zoneId];
      if (!zone || !zone.pricing[carName]) return;

      const ranges = [...zone.pricing[carName]];
      const infIndex = ranges.findIndex((r) => r.end === "INF");

      if (infIndex === -1) return;

      // Update only the price of the INF record
      ranges[infIndex] = {
        ...ranges[infIndex],
        price: Number(price),
      };

      state.zones[zoneId].pricing[carName] = ranges;
    },

    // form -- new city creation form
    setNewCityValue(state, action) {
      const cityName = action.payload.cityName;
      const country = action.payload.country;

      state.newCityCreationForm.cityName = cityName;
      state.newCityCreationForm.country = country;

      state.newCityCreationForm.cityNameErrorMessage = "";
      state.newCityCreationForm.cityExistErrorMessage = "";

      // Check for equal already exists with country
      if (
        Object.keys(state.cities).some(
          (key) =>
            cityName.toLowerCase() ===
              state.cities[key].cityName.toLowerCase() &&
            country.toLowerCase() === state.cities[key].country.toLowerCase()
        )
      ) {
        state.newCityCreationForm.cityExistErrorMessage = `${cityName} already created`;
      }
    },

    setNewCityValueCorrect(state, action) {
      state.newCityCreationForm.cityNameCorrect = !!action.payload.value;
    },

    setNewCityCurrencyValue(state, action) {
      state.newCityCreationForm.currency = action.payload.currency;
      state.newCityCreationForm.currencyErrorMessage = "";
    },

    setNewCityCurrencyValueCorrect(state, action) {
      state.newCityCreationForm.currencyCorrect = !!action.payload.value;
    },

    setDistanceUnit(state, action) {
      const payloadUnit = action.payload.unit;

      if (DISTANCE_UNIT.some((unit) => unit === payloadUnit))
        state.newCityCreationForm.distanceUnit = action.payload.unit;
    },

    // form -- new zone creation form
    setLocation(state, action) {
      const intent = action.payload.intent;
      const pointKey = action.payload.pointKey;
      const newValue = action.payload.newValue;

      let pointKeyCorrect = "pointACorrect"; // "pointACorrect" | "pointBCorrect"
      if (pointKey === "pointA") pointKeyCorrect = "pointACorrect";
      else pointKeyCorrect = "pointBCorrect";

      state.newZoneCreationForm[pointKey] = {
        ...newValue,
      };

      if (intent === "change")
        state.newZoneCreationForm[pointKeyCorrect] = false;

      if (
        intent === "set" &&
        newValue.lat !== null &&
        newValue.lng !== null &&
        newValue.place_id !== "" &&
        newValue.address !== ""
      )
        state.newZoneCreationForm[pointKeyCorrect] = true;
    },

    setZoneName(state, action) {
      state.newZoneCreationForm.zoneName = action.payload.newValue;

      if (state.newZoneCreationForm.zoneName.trim().length > 0) {
        state.newZoneCreationForm.errorMessage = "";
        state.newZoneCreationForm.zoneNameCorrect = true;
      } else {
        state.newZoneCreationForm.zoneNameCorrect = false;
        state.newZoneCreationForm.errorMessage = "Zone name can't be empty";
      }
    },

    setModeChange(state, action) {
      const newMode = action.payload.mode;

      if (
        newMode !== "" &&
        !EXTRANET_PRICING_MODES_ARRAY.some((mode) => mode === newMode)
      ) {
        return;
      }
      state.newZoneCreationForm.mode = action.payload.mode;
    },
  },
});

export const pricingReducer = pricingSlice.reducer;
export const {
  resetPricingSlice,

  // Helping Variables
  setNewCityCreationMode,
  selectCity,
  setPricingViewMode,

  // Cities
  setCitiesFetchingStatus,
  setCities,
  addCity,
  deleteExistingCity,
  setIsZoneError,
  setIsZoneFetching,
  setIsZoneFetched,
  incrementZoneRefetchCount,

  // Zones
  changeZoneStatus,
  resetZonePricingForm,

  // Zones Reducer Oneway
  addZone,
  setOnewayAccordianIndex,
  addCarInZone,
  removeCarInZone,
  changeOneWayZoneForm,
  removeZone,

  // Zone Reducer Hourly
  changeHourlyZoneForm,

  // Zone Reducer Long Distance
  addFirstLongDistanceRecord,
  addLongDistanceRecord,
  removeLongDistanceRecord,
  changeLongDistanceRecord,
  updateInfinityRecordPrice,

  // form -- new city creation form
  setNewCityValue,
  setNewCityValueCorrect,
  setNewCityCurrencyValue,
  setNewCityCurrencyValueCorrect,
  setDistanceUnit,

  // form -- new zone creation form
  setLocation,
  setZoneName,
  setModeChange,
} = pricingSlice.actions;
